import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  IconButton,
  Tooltip,
  Zoom,
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Card,
  CardContent,
  Collapse,
  Avatar,
  Stepper,
  Step,
  StepContent,
  StepButton,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Fab,
  Autocomplete,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import BusinessIcon from "@mui/icons-material/Business";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CategoryIcon from "@mui/icons-material/Category";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import SpeedIcon from "@mui/icons-material/Speed";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FlagIcon from "@mui/icons-material/Flag";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ShareIcon from "@mui/icons-material/Share";
import RefreshIcon from "@mui/icons-material/Refresh";
import { createNewTaskAPI, getStaffData } from "../../../Action/api";
import { getUserDataFromToken } from "../../../Utils/urlAuthHandler";
import { descriptionTemplates, expectedOutputTemplates } from "./taskTemplate";
import TaskLoader from "../../../Components/Reusable/UILoader/TaskLoader";
import supabase from "../../../Components/Database/Supabase";
import { generatePDF } from "./TaskPDF";
import {
  isWorkingDay,
  calculateDueDateWithWorkingDays,
  getWeekendStatusMessage,
  initializeWorkingDaysConfig,
  getNextWorkingDay,
} from "../../../Utils/workingDaysConfig";
import { isPublicHoliday, getHolidayDetails } from "../../../Utils/holidays";

// App primary color - Forest Green #228B22
const appColor = {
  primary: "#228B22",
  primaryLight: "#4caf50", // Lighter shade that complements the primary
  primaryDark: "#1b5e20", // Darker shade for contrast
  primaryFaded: "rgba(34, 139, 34, 0.1)", // For subtle backgrounds
  accent: "#8BC34A", // Complementary green for accents
  highlight: "#66BB6A", // For highlights
};

// Create MUI theme with custom colors
const customTheme = createTheme({
  palette: {
    primary: {
      main: appColor.primary,
      light: appColor.primaryLight,
      dark: appColor.primaryDark,
    },
    secondary: {
      main: "#558b2f", // A complementary green shade
      light: "#85bb5c",
      dark: "#33691e",
    },
  },
});

// Motivational quotes for productivity and task management
const quotes = [
  {
    text: "The key to productivity is not working harder, but working smarter.",
    author: "Anonymous",
  },
  { text: "Done is better than perfect.", author: "Sheryl Sandberg" },
  {
    text: "The way to get started is to quit talking and begin doing.",
    author: "Walt Disney",
  },
  {
    text: "You don't have to be great to start, but you have to start to be great.",
    author: "Zig Ziglar",
  },
  {
    text: "The secret of getting ahead is getting started.",
    author: "Mark Twain",
  },
  {
    text: "Amateurs sit and wait for inspiration, the rest of us just get up and go to work.",
    author: "Stephen King",
  },
  {
    text: "Productivity is never an accident. It is always the result of a commitment to excellence.",
    author: "Paul J. Meyer",
  },
  { text: "Focus on being productive instead of busy.", author: "Tim Ferriss" },
  {
    text: "Don't count the days. Make the days count.",
    author: "Muhammad Ali",
  },
  {
    text: "Your work is going to fill a large part of your life, and the only way to be truly satisfied is to do what you believe is great work.",
    author: "Steve Jobs",
  },
];

// Available departments
const departments = [
  "Product & Technology",
  "Marketing & Growth",
  "Sales & Business Dev",
  "Customer Success",
  "Operations",
  "Data & Analytics",
  "Legal & Compliance",
  "Finance",
  "Human Resources",
];

const CreateTask = () => {
  // Get user name from token
  const { userName: userNameFromToken } = getUserDataFromToken();
  const userName = userNameFromToken || "John Doe"; // Fallback if not found

  // Sample data for form reset - using actual user name from localStorage
  const sampleTaskData = [
    {
      title: "Implement User Authentication",
      description:
        "Create a secure login system with password reset functionality and two-factor authentication support.",
      expectedOutput:
        "A fully functional authentication system with login, registration, password reset, and 2FA support.",
      priority: "High Priority, High Complexity (5d)",
      createdBy: userName,
      assignedTo: "",
      numberOfPeople: "1",
      taskType: "Feature",
    },
    {
      title: "Optimize Database Queries",
      description:
        "Review and optimize the current database queries to improve application performance. Focus on the user dashboard and reporting sections.",
      expectedOutput:
        "50% reduction in query response time and decreased server load during peak hours.",
      priority: "High Priority, High Complexity (5d)",
      createdBy: userName,
      assignedTo: "",
      numberOfPeople: "1",
      taskType: "Improvements",
    },
    {
      title: "Fix Mobile Responsive Layout",
      description:
        "The dashboard layout is broken on mobile devices. Elements are overlapping and some buttons are not clickable on screens smaller than 375px width.",
      expectedOutput:
        "Fully responsive layout that works correctly on all device sizes from 320px up to desktop resolutions.",
      priority: "High Priority, High Complexity (5d)",
      createdBy: userName,
      assignedTo: "",
      numberOfPeople: "1",
      taskType: "Bug",
    },
  ];

  const [formData, setFormData] = useState({
    title: "Fix Login Bug", // Example task title
    description: "Investigate and resolve the login issue reported by users.", // Example description
    expectedOutput:
      "A bug-free login functionality with proper error handling.", // Example expected output
    startDate: "", // Will be set after loading holidays
    dueDate: "", // Will be calculated after start date is set
    priority: "High Priority, High Complexity (5d)", // Example priority
    createdBy: userName, // Use the name from localStorage
    assignedTo: "", // We'll store the name here for display
    assignedToUId: "", // uId for the selected person
    assignedToUserId: "", // userId for the selected person
    numberOfPeople: "1", // Example number of people working
    taskType: "Improvements", // Default task type
    department: "Product & Technology", // Default department
    TotalManDays: "5", // Default from High Priority, High Complexity (5d)
  });

  const [loading, setLoading] = useState(false); // Loading state for API call
  const [error, setError] = useState(null); // Error state for API call
  const [success, setSuccess] = useState(false); // Success state for API call
  const [openDescModal, setOpenDescModal] = useState(false); // State for description template modal
  const [openOutputModal, setOpenOutputModal] = useState(false); // State for expected output template modal
  const [showLoader, setShowLoader] = useState(false); // State for loader visibility
  const [showSampleDataAlert, setShowSampleDataAlert] = useState(true); // Show alert on initial load
  const [filteredDescTemplates, setFilteredDescTemplates] =
    useState(descriptionTemplates); // Filtered description templates
  const [filteredOutputTemplates, setFilteredOutputTemplates] = useState(
    expectedOutputTemplates
  ); // Filtered output templates
  const [expandedDesc, setExpandedDesc] = useState(false);
  const [expandedOutput, setExpandedOutput] = useState(false);
  const [copiedDesc, setCopiedDesc] = useState(false);
  const [copiedOutput, setCopiedOutput] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [randomQuote, setRandomQuote] = useState(
    quotes[Math.floor(Math.random() * quotes.length)]
  );
  const [formProgress, setFormProgress] = useState(0);
  const [people, setPeople] = useState([]); // Store people data from API
  const [peopleLoading, setPeopleLoading] = useState(false); // Loading state for people data

  // Add conflict modal state
  const [openConflictModal, setOpenConflictModal] = useState(false);
  const [conflictDetails, setConflictDetails] = useState(null);

  // Add PDF preview dialog state
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [pdfTaskData, setPdfTaskData] = useState(null);

  // Add state for field validation errors
  const [priorityError, setPriorityError] = useState(false);
  const [taskTypeError, setTaskTypeError] = useState(false);

  // Add state for weekend date warning
  const [weekendDateWarning, setWeekendDateWarning] = useState(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const priorities = [
    "High Priority, High Complexity (5d)",
    "High Priority, Medium Complexity (3d)",
    "High Priority, Low Complexity (1d)",
    "Medium Priority, High Complexity (4d)",
    "Medium Priority, Medium Complexity (2d)",
    "Medium Priority, Low Complexity (1d)",
    "Low Priority, High Complexity (5d)",
    "Low Priority, Medium Complexity (3d)",
    "Low Priority, Low Complexity (1d)",
  ];
  const taskTypes = ["Bug", "Idea", "Improvements", "Feature", "Others"];

  const userId = "12345"; // Replace with dynamic user ID

  const steps = [
    {
      label: "Basic Details",
      fields: ["title", "department", "description"],
      icon: <LightbulbIcon />,
    },
    {
      label: "Expected Results",
      fields: ["expectedOutput"],
      icon: <EmojiObjectsIcon />,
    },
    {
      label: "Timeline",
      fields: ["startDate", "dueDate", "priority"],
      icon: <AccessTimeIcon />,
    },
    {
      label: "Team & Type",
      fields: ["createdBy", "assignedTo", "numberOfPeople", "taskType"],
      icon: <GroupIcon />,
    },
  ];

  // Calculate form progress
  useEffect(() => {
    let filledFields = 0;
    let totalRequiredFields = 0;

    // Count filled required fields
    [
      "title",
      "department",
      "description",
      "expectedOutput",
      "startDate",
      "dueDate",
      "priority",
      "taskType",
      "createdBy",
      "assignedTo",
    ].forEach((field) => {
      totalRequiredFields++;
      if (formData[field] && formData[field].toString().trim() !== "") {
        filledFields++;
      }
    });

    setFormProgress((filledFields / totalRequiredFields) * 100);

    // Update completed steps
    const newCompleted = {};
    steps.forEach((step, index) => {
      const stepFields = step.fields;
      const stepCompleted = stepFields.every(
        (field) => formData[field] && formData[field].toString().trim() !== ""
      );
      if (stepCompleted) {
        newCompleted[index] = true;
      }
    });
    setCompletedSteps(newCompleted);

    // Auto-advance to the next step when current step is complete (but not on mobile)
    if (
      !isSmallScreen &&
      completedSteps[activeStep] &&
      activeStep < steps.length - 1
    ) {
      // Wait a moment before advancing
      const timer = setTimeout(() => {
        setActiveStep((prevStep) => prevStep + 1);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [formData, steps, activeStep, isSmallScreen, completedSteps]);

  // Show sample data alert and rotate quotes on component mount
  useEffect(() => {
    setShowSampleDataAlert(true);

    // Change quote periodically
    const quoteInterval = setInterval(() => {
      setRandomQuote(quotes[Math.floor(Math.random() * quotes.length)]);
    }, 10000); // Change every 10 seconds

    return () => clearInterval(quoteInterval);
  }, []);

  // Filter templates based on department selection
  useEffect(() => {
    const selectedDepartment = formData.department;

    // Filter description templates
    const filteredDesc = descriptionTemplates.filter(
      (template) =>
        !template.departments ||
        template.departments.includes(selectedDepartment)
    );
    setFilteredDescTemplates(filteredDesc);

    // Filter output templates
    const filteredOutput = expectedOutputTemplates.filter(
      (template) =>
        !template.departments ||
        template.departments.includes(selectedDepartment)
    );
    setFilteredOutputTemplates(filteredOutput);
  }, [formData.department]);

  // Function to fetch people data from API (reusable)
  const fetchPeopleData = async () => {
    try {
      console.log("Attempting to fetch staff data from API...");
      setPeopleLoading(true);

      // Fetch staff data using the new API
      const staffData = await getStaffData();

      console.log("API staff data:", staffData);

      // Transform data for use in the component
      // Filter out staff with 'NONE' as full name
      const formattedPeople = staffData
        .filter(
          (person) => person.staffFullName && person.staffFullName !== "NONE"
        )
        .map((person) => ({
          userId: person.userId,
          uId: person.uId,
          name: person.staffFullName,
          // Format display value to show name and uId
          displayValue: `${person.staffFullName} (${person.uId})`,
        }));

      console.log("Formatted Staff Data:", formattedPeople);
      setPeople(formattedPeople);
    } catch (error) {
      console.error("Error fetching staff data:", error);
      // Set an empty array if there's an error
      setPeople([]);
    } finally {
      setPeopleLoading(false);
    }
  };

  // Fetch people data from API on component mount
  useEffect(() => {
    fetchPeopleData();

    // Initialize working days configuration from API (holidays and weekend settings)
    initializeWorkingDaysConfig("IN")
      .then(() => {
        // After holidays are loaded, set the default start date to next working day
        const today = new Date();
        let defaultStartDate;

        // Check if today is a working day
        if (isWorkingDay(today)) {
          defaultStartDate = today;
        } else {
          // Get the next working day
          defaultStartDate = getNextWorkingDay(today);
        }

        // Format date as YYYY-MM-DD
        const year = defaultStartDate.getFullYear();
        const month = String(defaultStartDate.getMonth() + 1).padStart(2, "0");
        const day = String(defaultStartDate.getDate()).padStart(2, "0");
        const formattedStartDate = `${year}-${month}-${day}`;

        // Calculate due date based on the default priority (5 days)
        const daysToAdd = 5; // From "High Priority, High Complexity (5d)"
        const calculatedDueDate = calculateDueDateWithWorkingDays(
          defaultStartDate,
          daysToAdd
        );

        // Format due date
        const dueYear = calculatedDueDate.getFullYear();
        const dueMonth = String(calculatedDueDate.getMonth() + 1).padStart(
          2,
          "0"
        );
        const dueDay = String(calculatedDueDate.getDate()).padStart(2, "0");
        const formattedDueDate = `${dueYear}-${dueMonth}-${dueDay}`;

        // Update form data with valid working day dates
        setFormData((prevData) => ({
          ...prevData,
          startDate: formattedStartDate,
          dueDate: formattedDueDate,
        }));
      })
      .catch((error) => {
        console.error("Failed to initialize working days config:", error);
        // Fallback to today if initialization fails
        const today = new Date().toISOString().split("T")[0];
        const dueDate = new Date(new Date().setDate(new Date().getDate() + 4))
          .toISOString()
          .split("T")[0];
        setFormData((prevData) => ({
          ...prevData,
          startDate: today,
          dueDate: dueDate,
        }));
      });
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    // If user changes any field, hide the sample data alert
    if (showSampleDataAlert) {
      setShowSampleDataAlert(false);
    }

    const { name, value } = e.target;

    // Calculate due date based on start date and priority (considering working days)
    const calculateDueDate = (startDateValue, priorityValue) => {
      if (!startDateValue || !priorityValue) return null;

      const daysToAdd = extractDaysFromPriority(priorityValue);
      const startDate = new Date(startDateValue + "T00:00:00"); // Add time to avoid timezone issues

      // Use working days calculation that skips weekends
      const dueDate = calculateDueDateWithWorkingDays(startDate, daysToAdd);

      // Format date as YYYY-MM-DD without timezone conversion
      const year = dueDate.getFullYear();
      const month = String(dueDate.getMonth() + 1).padStart(2, "0");
      const day = String(dueDate.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    // Update form data with the new value
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Handle different field changes
    if (name === "priority") {
      // Calculate new due date based on the selected priority and current start date
      const newDueDate = calculateDueDate(formData.startDate, value);

      if (newDueDate) {
        // Get days directly from priority - this is the key fix
        const daysToAdd = extractDaysFromPriority(value);

        // Update due date and man days in the form
        setFormData((prevData) => ({
          ...prevData,
          [name]: value,
          dueDate: newDueDate,
          // Use exact days from priority for TotalManDays
          TotalManDays: daysToAdd.toString(),
        }));

        // Debug info
        console.log(`Priority selected: ${value}`);
        console.log(`Days extracted: ${daysToAdd}`);
        console.log(`Total man days set: ${daysToAdd}`);
      }
    } else if (name === "startDate") {
      // At this point, the date has already been validated as a working day
      // Calculate new due date based on the start date and current priority
      const newDueDate = calculateDueDate(value, formData.priority);

      if (newDueDate) {
        // Get days directly from priority
        const daysToAdd = extractDaysFromPriority(formData.priority);

        // Update due date and man days in the form
        setFormData((prevData) => ({
          ...prevData,
          [name]: value,
          dueDate: newDueDate,
          // Use exact days from priority for TotalManDays
          TotalManDays: daysToAdd.toString(),
        }));

        // Debug info
        console.log(`Start date changed, priority: ${formData.priority}`);
        console.log(`Days extracted: ${daysToAdd}`);
        console.log(`Total man days set: ${daysToAdd}`);
      }
    }
  };

  // Reference kept for potential future use
  const suggestionsRef = useRef(null);

  // Extract days from the selected priority if available
  const extractDaysFromPriority = (priorityValue) => {
    // Try to extract days from the pattern like "High Priority, Medium Complexity (3d)"
    const daysMatch = priorityValue.match(/\((\d+)d\)/);
    if (daysMatch && daysMatch[1]) {
      return parseInt(daysMatch[1], 10);
    }

    // Define proper mapping for all priority-complexity combinations
    const priorityDaysMap = {
      // High Priority combinations
      "High Priority, High Complexity (5d)": 5,
      "High Priority, Medium Complexity (3d)": 3,
      "High Priority, Low Complexity (1d)": 1,

      // Medium Priority combinations
      "Medium Priority, High Complexity (4d)": 4,
      "Medium Priority, Medium Complexity (2d)": 2,
      "Medium Priority, Low Complexity (1d)": 1,

      // Low Priority combinations
      "Low Priority, High Complexity (5d)": 5,
      "Low Priority, Medium Complexity (3d)": 3,
      "Low Priority, Low Complexity (1d)": 1,

      // Legacy/shorthand priority values
      "High-HC": 5,
      "High-MC": 3,
      "High-LC": 1,
      "Med-HC": 4,
      "Med-MC": 2,
      "Med-LC	": 1,
      "Low-HC": 5,
      "Low-MC": 3,
      "Low-LC": 1,

      // // Very old legacy values
      // 'High': 3,
      // 'Medium': 2,
      // 'Low': 3,
      // 'Critical': 1
    };

    // Return the mapped days or default to 3 days if not found
    //return priorityDaysMap[priorityValue] || 3;
    return priorityDaysMap[priorityValue];
  };

  // Reset form with sample data
  const resetWithSampleData = () => {
    // Select a random sample data
    const randomIndex = Math.floor(Math.random() * sampleTaskData.length);
    const sampleData = sampleTaskData[randomIndex];

    // Calculate dates based on current date or next working day
    const today = new Date();
    let startDate;

    // Check if today is a working day
    if (isWorkingDay(today)) {
      startDate = today;
    } else {
      // Get the next working day
      startDate = getNextWorkingDay(today);
    }

    // Format start date as YYYY-MM-DD
    const year = startDate.getFullYear();
    const month = String(startDate.getMonth() + 1).padStart(2, "0");
    const day = String(startDate.getDate()).padStart(2, "0");
    const formattedStartDate = `${year}-${month}-${day}`;

    // Alex
    const priorityMapping = {
      "High-HC": "High Priority, High Complexity (5d)",
      "High-MC": "High Priority, Medium Complexity (3d)",
      "High-LC": "High Priority, Low Complexity (1d)",

      "Med-HC": "Medium Priority, High Complexity (4d)",
      "Med-MC": "Medium Priority, Medium Complexity (2d)",
      "Med-LC	": "Medium Priority, Low Complexity (1d)",

      "Low-HC": "Low Priority, High Complexity (5d)",
      "Low-MC": "Low Priority, Medium Complexity (3d)",
      "Low-LC": "Low Priority, Low Complexity (1d)",
    };

    // Get priority in new format
    const newPriority =
      priorityMapping[sampleData.priority] ||
      "Medium Priority, Medium Complexity (2d)";

    // Extract days from the pattern like "High Priority, Medium Complexity (3d)"
    let daysToAdd = 2; // Default fallback
    const daysMatch = newPriority.match(/\((\d+)d\)/);
    if (daysMatch && daysMatch[1]) {
      daysToAdd = parseInt(daysMatch[1], 10);
    }

    // Calculate due date using working days
    const calculatedDueDate = calculateDueDateWithWorkingDays(
      startDate,
      daysToAdd
    );

    // Format due date
    const dueYear = calculatedDueDate.getFullYear();
    const dueMonth = String(calculatedDueDate.getMonth() + 1).padStart(2, "0");
    const dueDay = String(calculatedDueDate.getDate()).padStart(2, "0");
    const formattedDueDate = `${dueYear}-${dueMonth}-${dueDay}`;

    const taskType = sampleData.taskType || "Idea";

    // Randomly select a department
    const randomDept =
      departments[Math.floor(Math.random() * departments.length)];

    // Set the form data with sample data and updated priority format
    setFormData({
      ...sampleData,
      startDate: formattedStartDate,
      dueDate: formattedDueDate,
      department: randomDept,
      priority: newPriority, // Use the new priority format
      taskType: taskType, // Ensure task type is set
    });

    // Clear any previous task type error
    setTaskTypeError(false);

    // Show the sample data alert
    //setShowSampleDataAlert(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that priority is selected
    if (!formData.priority || formData.priority.trim() === "") {
      setPriorityError(true);
      // Switch to the Timeline step where priority field is located
      setActiveStep(2);
      return; // Stop form submission
    }

    // Validate that task type is selected
    if (!formData.taskType || formData.taskType.trim() === "") {
      setTaskTypeError(true);
      // Switch to the Team & Type step where task type field is located
      setActiveStep(3);
      return; // Stop form submission
    }
    setShowLoader(true); // Show the loader
    setLoading(true);
    setError(null);
    setSuccess(false);
    setPriorityError(false); // Clear any previous priority error

    try {
      // Simulate network delay to showcase the loader (remove in production)
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Make sure TotalManDays is calculated before submission
      const startDate = formData.startDate;
      const dueDate = formData.dueDate;
      const TotalManDays =
        formData.TotalManDays ||
        calculateManDays(startDate, dueDate).toString();

      // Prepare the payload with TotalManDays included
      const taskPayload = {
        ...formData,
        TotalManDays,
      };

      // 1. Create the task
      const response = await createNewTaskAPI(userId, taskPayload); // Call the API method with updated payload

      // Log the full response for debugging
      console.log("API Response:", response);

      // Check if the response is an error object from axios
      if (response.isAxiosError) {
        // For axios errors, the response data is in response.response.data
        console.log("API Error Response:", response.response);

        if (response.response && response.response.status === 409) {
          // Extract details from the error response
          const errorData = response.response.data;
          const existingTask = errorData.existingTask;

          // Set conflict details and show the conflict modal
          setConflictDetails(existingTask);
          setOpenConflictModal(true);

          // Don't throw the error, as we'll show it in the modal
          setLoading(false);
          setShowLoader(false);
          return;
        } else {
          // For other errors, throw a general error
          throw new Error(
            `Error: ${response.message || "Unknown error occurred"}`
          );
        }
      }

      if (response.status !== 200) {
        throw new Error(`Error: ${response.statusText}`);
      }

      console.log("Task Created Successfully:", response.data);

      // 2. Update the task count in 003_People_Master table
      try {
        // Get the assignee's userId from the form
        const loginIdToUpdate = formData.assignedToUserId; // Use userId from the selected staff

        // First, get the current count
        const { data: currentData, error: fetchError } = await supabase
          .from("003_People_Master")
          .select("task_to_do_count")
          .eq("login_id", loginIdToUpdate)
          .single();

        if (fetchError) throw fetchError;

        // Calculate the new count (increment by 1)
        const currentCount = currentData?.task_to_do_count || 0;
        const newCount = currentCount + 1;

        // Update the count in the database
        const { error: updateError } = await supabase
          .from("003_People_Master")
          .update({ task_to_do_count: newCount })
          .eq("login_id", loginIdToUpdate);

        if (updateError) throw updateError;

        console.log(
          `Task count updated for ${loginIdToUpdate}: ${currentCount} → ${newCount}`
        );
      } catch (countError) {
        console.error("Error updating task count:", countError);
        // We don't want to fail the entire process if just the count update fails
      }

      setSuccess(true);

      // Store task data for PDF generation and show the PDF preview dialog
      const taskDataForPdf = {
        ...taskPayload,
        TaskID: response.data?.taskID,
        CreatedAt: new Date().toISOString(),
      };
      setPdfTaskData(taskDataForPdf);
      setOpenPdfDialog(true);

      // Reset form with sample data instead of clearing it
      resetWithSampleData();
    } catch (err) {
      console.error("Error creating task:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setShowLoader(false); // Hide the loader
    }
  };

  // Handle PDF preview generation
  const handlePdfPreview = () => {
    if (pdfTaskData) {
      generatePDF(pdfTaskData);
    }
    setOpenPdfDialog(false);
  };

  // Handle PDF download function
  const handlePdfDownload = () => {
    if (pdfTaskData) {
      // Format assignedTo name for the filename (remove spaces, special characters)
      const formattedAssignee = pdfTaskData.assignedTo
        ? pdfTaskData.assignedTo.replace(/[^a-zA-Z0-9]/g, "_")
        : "Unassigned";

      // Create a proper filename with task number and assignee exactly as required (taskNumber_assignedTo.pdf)
      const filename = `${
        pdfTaskData.TaskID || "New"
      }_${formattedAssignee}.pdf`;

      console.log("Downloading PDF with filename:", filename);

      // Generate the PDF with direct download option
      generatePDF(pdfTaskData, {
        download: true,
        filename: filename,
      });
    }
    setOpenPdfDialog(false);
  };

  // Handle PDF sharing function
  const handleSharePdf = () => {
    if (pdfTaskData) {
      // Generate the PDF and then try to share it if Web Share API is available
      generatePDF(pdfTaskData, {
        download: true,
        filename: `Task-${pdfTaskData.TaskID || "New"}.pdf`,
        onComplete: (pdfUrl) => {
          if (navigator.share) {
            navigator
              .share({
                title: `Task: ${pdfTaskData.title || "New Task"}`,
                text: "Task assignment details",
                url: pdfUrl || window.location.href,
              })
              .catch((error) => console.error("Error sharing:", error));
          } else {
            // Fallback for browsers that don't support the Web Share API
            const emailBody = `Please find the task details for: ${
              pdfTaskData.title || "New Task"
            }.\n\nView it online at: ${window.location.href}`;
            window.open(
              `mailto:?subject=Task Assignment: ${
                pdfTaskData.title || "New Task"
              }&body=${encodeURIComponent(emailBody)}`
            );
          }
        },
      });
    }
    setOpenPdfDialog(false);
  };

  // Handle selecting a description template
  const handleSelectDescriptionTemplate = (templateId) => {
    const selectedTemplate = filteredDescTemplates.find(
      (template) => template.id === templateId
    );
    if (selectedTemplate) {
      setFormData((prevData) => ({
        ...prevData,
        description: selectedTemplate.template,
      }));
      setOpenDescModal(false);
    }
  };

  // Handle selecting an expected output template
  const handleSelectOutputTemplate = (templateId) => {
    const selectedTemplate = filteredOutputTemplates.find(
      (template) => template.id === templateId
    );
    if (selectedTemplate) {
      setFormData((prevData) => ({
        ...prevData,
        expectedOutput: selectedTemplate.template,
      }));
      setOpenOutputModal(false);
    }
  };

  // Quick actions for the current step
  const getQuickAction = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setOpenDescModal(true)}
            size="small"
            sx={{ mt: 1 }}
            disabled={filteredDescTemplates.length === 0}
          >
            Use Template
          </Button>
        );
      case 1:
        return (
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setOpenOutputModal(true)}
            size="small"
            color="secondary"
            sx={{ mt: 1 }}
            disabled={filteredOutputTemplates.length === 0}
          >
            Use Template
          </Button>
        );
      case 2:
        const today = new Date();
        let quickStartDate;

        // Check if today is a working day
        if (isWorkingDay(today)) {
          quickStartDate = today;
        } else {
          // Get the next working day
          quickStartDate = getNextWorkingDay(today);
        }

        // Calculate 7 working days from the start date
        const quickDueDate = calculateDueDateWithWorkingDays(quickStartDate, 7);

        return (
          <Button
            variant="outlined"
            startIcon={<AccessTimeIcon />}
            size="small"
            onClick={() => {
              setFormData({
                ...formData,
                startDate: quickStartDate.toISOString().split("T")[0],
                dueDate: quickDueDate.toISOString().split("T")[0],
              });
            }}
            sx={{ mt: 1 }}
          >
            Set Next Week
          </Button>
        );
      default:
        return null;
    }
  };

  // Helper functions

  // Calculate man days between two dates (excluding weekends)
  const calculateManDays = (startDateStr, endDateStr) => {
    try {
      // Parse dates and ensure they are valid
      const startDate = new Date(startDateStr);
      const endDate = new Date(endDateStr);

      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.warn(
          "Invalid date formats in calculateManDays: ",
          startDateStr,
          endDateStr
        );
        return 1; // Default to 1 if dates are invalid
      }

      // Normalize dates to start of day to avoid time-related issues
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      // If start date is after end date, return 1 as minimum
      if (startDate > endDate) {
        console.warn("Start date is after end date in calculateManDays");
        return 1;
      }

      // Same day case - if it's not a weekend, count as 1 day
      if (startDate.getTime() === endDate.getTime()) {
        const dayOfWeek = startDate.getDay();
        return dayOfWeek !== 0 && dayOfWeek !== 6 ? 1 : 0;
      }

      // Calculate business days between dates (inclusive of start and end date)
      let totalBusinessDays = 0;
      const currentDate = new Date(startDate);

      // Loop through each day including the end date (using <= for inclusive count)
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay();

        // Count only weekdays (0 = Sunday, 6 = Saturday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          totalBusinessDays++;
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Ensure we always return at least 1 man day for valid date ranges
      return Math.max(1, totalBusinessDays);
    } catch (error) {
      console.error("Error calculating man days:", error);
      return 1; // Default to 1 man day in case of error
    }
  };

  // Handle step change
  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  // Copy to clipboard function
  const handleCopyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      if (field === "description") {
        setCopiedDesc(true);
        setTimeout(() => setCopiedDesc(false), 2000);
      } else if (field === "output") {
        setCopiedOutput(true);
        setTimeout(() => setCopiedOutput(false), 2000);
      }
    });
  };

  // Clear field function
  const handleClearField = (field) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: "",
    }));
  };

  return (
    <ThemeProvider theme={customTheme}>
      <style>
        {`
          /* Add visual styling for date picker to indicate weekends should not be selected */
          input[type="date"]::-webkit-calendar-picker-indicator {
            cursor: pointer;
          }
          
          /* Visual feedback for invalid date selection */
          input[type="date"]:invalid {
            border-color: #f44336 !important;
          }
        `}
      </style>
      <Box
        sx={{
          padding: { xs: 2, sm: 4 },
          backgroundColor: appColor.primaryFaded,
          backgroundImage: `linear-gradient(135deg, #f5f7fa 0%, ${appColor.primaryFaded} 100%)`,
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Task Loader */}
        {showLoader && (
          <TaskLoader message="Creating your task and preparing the next one..." />
        )}

        <Paper
          elevation={4}
          sx={{
            padding: { xs: 2, sm: 4 },
            borderRadius: 3,
            maxWidth: 1000,
            width: "100%",
            backgroundImage:
              "linear-gradient(to top, #f3f3f3 0%, #ffffff 100%)",
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Decorative elements */}
          <Box
            sx={{
              position: "absolute",
              top: -20,
              right: -20,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${appColor.primaryFaded} 0%, rgba(34, 139, 34, 0) 70%)`,
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -30,
              left: -30,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${appColor.primaryFaded} 0%, rgba(34, 139, 34, 0) 70%)`,
              zIndex: 0,
            }}
          />

          <Typography
            variant="h4"
            sx={{
              fontWeight: "600",
              marginBottom: 3,
              textAlign: "center",
              background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.accent})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              position: "relative",
              zIndex: 1,
            }}
          >
            Create New Task
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "normal",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mt: 0.5,
              }}
            >
              Bring your ideas to action with well-defined tasks
            </Typography>
          </Typography>

          {/* Form Progress Indicator */}
          <Box sx={{ mb: 3, position: "relative" }}>
            <LinearProgress
              variant="determinate"
              value={formProgress}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "rgba(0,0,0,0.05)",
                "& .MuiLinearProgress-bar": {
                  background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.accent})`,
                  borderRadius: 4,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: "block",
                textAlign: "right",
                color: "text.secondary",
              }}
            >
              {Math.round(formProgress)}% complete
            </Typography>
          </Box>

          {/* Sample Data Alert */}
          {showSampleDataAlert && (
            <Alert
              severity="info"
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                animation: "fadeIn 0.5s",
                "& .MuiAlert-icon": { color: appColor.primary },
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "translateY(-10px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
              onClose={() => setShowSampleDataAlert(false)}
              icon={<StarIcon fontSize="inherit" />}
            >
              <AlertTitle sx={{ fontWeight: "bold" }}>
                Sample Data Loaded
              </AlertTitle>
              This form has been filled with sample data. Please review and
              modify before creating a new task.
            </Alert>
          )}

          {/* Weekend Date Warning Alert */}
          {weekendDateWarning && (
            <Alert
              severity="warning"
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                animation: "fadeIn 0.5s",
                "& .MuiAlert-icon": { color: "#ff9800" },
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "translateY(-10px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
              onClose={() => setWeekendDateWarning(null)}
              icon={<EventBusyIcon fontSize="inherit" />}
            >
              <AlertTitle sx={{ fontWeight: "bold" }}>
                Non-Working Day Detected
              </AlertTitle>
              {weekendDateWarning}
            </Alert>
          )}

          {/* Form with Stepper UI */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                {/* Vertical Stepper Navigation */}
                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    pr: 2,
                    borderRight: "1px solid",
                    borderColor: "divider",
                    height: "100%",
                  }}
                >
                  <Stepper
                    activeStep={activeStep}
                    orientation="vertical"
                    nonLinear
                    sx={{
                      "& .MuiStepLabel-label": {
                        fontWeight: (theme) =>
                          activeStep === 0 ? "bold" : "normal",
                      },
                      "& .MuiStepIcon-root.Mui-active": {
                        color: appColor.primary,
                      },
                      "& .MuiStepIcon-root.Mui-completed": {
                        color: appColor.primaryLight,
                      },
                    }}
                  >
                    {steps.map((step, index) => (
                      <Step key={step.label} completed={completedSteps[index]}>
                        <StepButton
                          onClick={() => handleStepChange(index)}
                          sx={{ justifyContent: "flex-start" }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                mr: 1,
                                bgcolor:
                                  activeStep === index
                                    ? index === 0
                                      ? appColor.primary
                                      : index === 1
                                      ? appColor.accent
                                      : index === 2
                                      ? appColor.primaryLight
                                      : appColor.highlight
                                    : "action.disabledBackground",
                                color:
                                  activeStep === index
                                    ? "white"
                                    : "text.secondary",
                                transition: "all 0.3s ease",
                              }}
                            >
                              {step.icon}
                            </Avatar>
                            {step.label}
                          </Box>
                        </StepButton>
                        <StepContent>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 4, pl: 1 }}
                          >
                            {index === 0 && "Define your task's basic details"}
                            {index === 1 && "Specify what success looks like"}
                            {index === 2 && "Set deadlines and priority"}
                            {index === 3 && "Assign people and categorize"}
                          </Typography>
                          {getQuickAction()}
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>

                  <Box sx={{ mt: 4, textAlign: "center" }}>
                    <SpeedIcon
                      sx={{
                        fontSize: 40,
                        opacity: 0.5,
                        mb: 1,
                        color: appColor.primary,
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Time saved with templates:
                    </Typography>
                    <Typography variant="h6" sx={{ color: appColor.primary }}>
                      ~15 minutes
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Horizontal Stepper for Mobile */}
              <Box
                sx={{
                  width: "100%",
                  display: { xs: "block", md: "none" },
                  mb: 3,
                }}
              >
                <Stepper activeStep={activeStep} alternativeLabel nonLinear>
                  {steps.map((step, index) => (
                    <Step
                      key={step.label}
                      completed={completedSteps[index]}
                      sx={{
                        "& .MuiStepIcon-root.Mui-active": {
                          color: appColor.primary,
                        },
                        "& .MuiStepIcon-root.Mui-completed": {
                          color: appColor.primaryLight,
                        },
                      }}
                    >
                      <StepButton onClick={() => handleStepChange(index)}>
                        {step.label}
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              {/* Form Fields Based on Current Step */}
              <Grid item xs={12} md={9}>
                <Box hidden={activeStep !== 0}>
                  <Grid container spacing={3}>
                    {/* Title Field */}
                    <Grid item xs={12}>
                      <TextField
                        label="Title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                        required
                        variant="outlined"
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    </Grid>

                    {/* Department Selection */}
                    <Grid item xs={12}>
                      <FormControl fullWidth required variant="outlined">
                        <InputLabel id="department-label">
                          Department
                        </InputLabel>
                        <Select
                          labelId="department-label"
                          name="department"
                          value={formData.department}
                          onChange={handleChange}
                          startAdornment={
                            <BusinessIcon
                              sx={{ mr: 1, color: "action.active" }}
                            />
                          }
                          label="Department"
                          sx={{ borderRadius: 2 }}
                        >
                          {departments.map((dept) => (
                            <MenuItem key={dept} value={dept}>
                              {dept}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Description Field with Template Button and Expandable View */}
                    <Grid item xs={12}>
                      <Card
                        variant="outlined"
                        sx={{
                          border: "1px solid",
                          borderColor: expandedDesc
                            ? appColor.primary
                            : "divider",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                        }}
                      >
                        <CardContent sx={{ pb: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight="medium"
                              color="text.primary"
                            >
                              Description
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Tooltip
                                title={`Select from templates for ${formData.department}`}
                                arrow
                                placement="top"
                                TransitionComponent={Zoom}
                              >
                                <span>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setOpenDescModal(true)}
                                    startIcon={<LibraryBooksIcon />}
                                    disabled={
                                      filteredDescTemplates.length === 0
                                    }
                                    size="small"
                                    sx={{
                                      borderRadius: "20px",
                                      boxShadow:
                                        "0 2px 6px rgba(0, 0, 0, 0.15)",
                                      padding: "4px 12px",
                                      transition: "all 0.3s ease",
                                      fontWeight: 500,
                                      fontSize: "0.75rem",
                                      mr: 1,
                                      "&:hover": {
                                        transform: "translateY(-1px)",
                                        boxShadow:
                                          "0 4px 8px rgba(0, 0, 0, 0.2)",
                                        background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.primaryLight})`,
                                      },
                                    }}
                                  >
                                    Templates{" "}
                                    {filteredDescTemplates.length > 0 &&
                                      `(${filteredDescTemplates.length})`}
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip
                                title="Copy description to clipboard"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyToClipboard(
                                      formData.description,
                                      "description"
                                    )
                                  }
                                  sx={{ mr: 0.5 }}
                                >
                                  <ContentCopyIcon
                                    fontSize="small"
                                    color={copiedDesc ? "primary" : "action"}
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title="Clear description"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleClearField("description")
                                  }
                                  sx={{ mr: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" color="action" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={expandedDesc ? "Collapse" : "Expand"}
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => setExpandedDesc(!expandedDesc)}
                                >
                                  {expandedDesc ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          <TextField
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={expandedDesc ? 12 : 5}
                            required
                            placeholder="Describe the task in detail"
                            variant="outlined"
                            InputProps={{
                              sx: {
                                fontSize: "0.95rem",
                                fontFamily: "monospace",
                                whiteSpace: "pre-wrap",
                                borderRadius: 2,
                              },
                            }}
                          />

                          {/* Additional description info */}
                          <Collapse in={expandedDesc}>
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: appColor.primaryFaded,
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Use this field to provide detailed information
                                about what needs to be done. Include necessary
                                context, requirements, and any specific
                                instructions that would help complete the task
                                successfully.
                              </Typography>
                            </Box>
                          </Collapse>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>

                <Box hidden={activeStep !== 1}>
                  <Grid container spacing={3}>
                    {/* Expected Output Field with Template Button and Expandable View */}
                    <Grid item xs={12}>
                      <Card
                        variant="outlined"
                        sx={{
                          border: "1px solid",
                          borderColor: expandedOutput
                            ? "secondary.main"
                            : "divider",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                        }}
                      >
                        <CardContent sx={{ pb: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight="medium"
                              color="text.primary"
                            >
                              Expected Output
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Tooltip
                                title={`Select from output templates for ${formData.department}`}
                                arrow
                                placement="top"
                                TransitionComponent={Zoom}
                              >
                                <span>
                                  <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => setOpenOutputModal(true)}
                                    startIcon={<FormatListBulletedIcon />}
                                    disabled={
                                      filteredOutputTemplates.length === 0
                                    }
                                    size="small"
                                    sx={{
                                      borderRadius: "20px",
                                      boxShadow:
                                        "0 2px 6px rgba(0, 0, 0, 0.15)",
                                      padding: "4px 12px",
                                      transition: "all 0.3s ease",
                                      fontWeight: 500,
                                      fontSize: "0.75rem",
                                      mr: 1,
                                      "&:hover": {
                                        transform: "translateY(-1px)",
                                        boxShadow:
                                          "0 4px 8px rgba(0, 0, 0, 0.2)",
                                        background:
                                          "linear-gradient(45deg, #558b2f, #8bc34a)",
                                      },
                                    }}
                                  >
                                    Templates{" "}
                                    {filteredOutputTemplates.length > 0 &&
                                      `(${filteredOutputTemplates.length})`}
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip
                                title="Copy expected output to clipboard"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyToClipboard(
                                      formData.expectedOutput,
                                      "output"
                                    )
                                  }
                                  sx={{ mr: 0.5 }}
                                >
                                  <ContentCopyIcon
                                    fontSize="small"
                                    color={
                                      copiedOutput ? "secondary" : "action"
                                    }
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title="Clear expected output"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleClearField("expectedOutput")
                                  }
                                  sx={{ mr: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" color="action" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={expandedOutput ? "Collapse" : "Expand"}
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setExpandedOutput(!expandedOutput)
                                  }
                                >
                                  {expandedOutput ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          <TextField
                            name="expectedOutput"
                            value={formData.expectedOutput}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={expandedOutput ? 12 : 5}
                            required
                            placeholder="Define what the completed task should deliver"
                            variant="outlined"
                            InputProps={{
                              sx: {
                                fontSize: "0.95rem",
                                fontFamily: "monospace",
                                whiteSpace: "pre-wrap",
                                borderRadius: 2,
                              },
                            }}
                          />

                          {/* Additional output info */}
                          <Collapse in={expandedOutput}>
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: "rgba(85, 139, 47, 0.1)",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Clearly define the deliverables and success
                                criteria for this task. What does "done" look
                                like? Include specific outputs, metrics, or
                                artifacts that should result from completing the
                                task.
                              </Typography>
                            </Box>
                          </Collapse>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Quick Tips */}
                    <Grid item xs={12}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          bgcolor: "rgba(85, 139, 47, 0.05)",
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{ display: "flex", alignItems: "flex-start" }}
                          >
                            <EmojiObjectsIcon
                              color="secondary"
                              sx={{ mr: 1 }}
                            />
                            <Box>
                              <Typography variant="subtitle2" color="secondary">
                                Pro Tip
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Great expected outputs are specific, measurable,
                                and clearly indicate what success looks like.
                                Try using action verbs and quantifiable metrics
                                where possible.
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>

                <Box hidden={activeStep !== 2}>
                  <Grid container spacing={3}>
                    {/* Priority Field - Moved to the top */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
                        <CardContent>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: appColor.primaryDark,
                              mb: 2,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <PriorityHighIcon fontSize="small" sx={{ mr: 1 }} />{" "}
                            Priority Level &amp; Complexity
                          </Typography>

                          <FormControl component="fieldset" fullWidth required>
                            <RadioGroup
                              name="priority"
                              value={formData.priority}
                              onChange={handleChange}
                            >
                              {/* High Priority Options */}
                              <Typography
                                variant="subtitle2"
                                color="error.main"
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1.5,
                                  display: "flex",
                                  alignItems: "center",
                                  borderLeft: "4px solid",
                                  borderColor: "error.main",
                                  pl: 1.5,
                                  py: 0.5,
                                  borderRadius: "0 4px 4px 0",
                                  bgcolor: "rgba(211, 47, 47, 0.04)",
                                }}
                              >
                                <FlagIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: "error.main" }}
                                />
                                High Priority
                              </Typography>
                              <Box
                                sx={{
                                  mb: 3,
                                  border: "1px solid",
                                  borderColor: "error.light",
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                  "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  },
                                }}
                              >
                                <Grid container>
                                  {priorities
                                    .filter((p) =>
                                      p.startsWith("High Priority")
                                    )
                                    .map((priority) => (
                                      <Grid item xs={12} md={4} key={priority}>
                                        <FormControlLabel
                                          value={priority}
                                          control={
                                            <Radio
                                              sx={{
                                                color: "error.main",
                                                "&.Mui-checked": {
                                                  color: "error.main",
                                                },
                                              }}
                                            />
                                          }
                                          label={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight:
                                                    formData.priority ===
                                                    priority
                                                      ? "bold"
                                                      : "medium",
                                                  color:
                                                    formData.priority ===
                                                    priority
                                                      ? "error.main"
                                                      : "text.primary",
                                                }}
                                              >
                                                {priority
                                                  .split(",")[1]
                                                  .trim()
                                                  .replace(/\s*\([^)]*\)/, "")}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "error.main",
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  mt: 0.5,
                                                }}
                                              >
                                                <AccessTimeIcon
                                                  sx={{ fontSize: 14, mr: 0.5 }}
                                                />
                                                {
                                                  priority.match(
                                                    /\(([^)]+)\)/
                                                  )[1]
                                                }
                                              </Typography>
                                            </Box>
                                          }
                                          sx={{
                                            width: "100%",
                                            m: 0,
                                            p: 1.5,
                                            borderRight: {
                                              xs: "none",
                                              md: "1px solid",
                                            },
                                            borderBottom: {
                                              xs: "1px solid",
                                              md: "none",
                                            },
                                            borderColor: "divider",
                                            "&:last-child": {
                                              borderRight: "none",
                                              borderBottom: "none",
                                            },
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                              bgcolor:
                                                "rgba(211, 47, 47, 0.08)",
                                              transform: "translateY(-2px)",
                                            },
                                            ...(formData.priority === priority
                                              ? {
                                                  bgcolor:
                                                    "rgba(211, 47, 47, 0.12)",
                                                  borderLeft: "4px solid",
                                                  borderLeftColor: "error.main",
                                                  pl: 1.5,
                                                }
                                              : {}),
                                          }}
                                        />
                                      </Grid>
                                    ))}
                                </Grid>
                              </Box>

                              {/* Medium Priority Options */}
                              <Typography
                                variant="subtitle2"
                                color={appColor.primary}
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1.5,
                                  display: "flex",
                                  alignItems: "center",
                                  borderLeft: "4px solid",
                                  borderColor: appColor.primary,
                                  pl: 1.5,
                                  py: 0.5,
                                  borderRadius: "0 4px 4px 0",
                                  bgcolor: appColor.primaryFaded,
                                }}
                              >
                                <FlagIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: appColor.primary }}
                                />
                                Medium Priority
                              </Typography>
                              <Box
                                sx={{
                                  mb: 3,
                                  border: "1px solid",
                                  borderColor: appColor.primaryLight,
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                  "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  },
                                }}
                              >
                                <Grid container>
                                  {priorities
                                    .filter((p) =>
                                      p.startsWith("Medium Priority")
                                    )
                                    .map((priority) => (
                                      <Grid item xs={12} md={4} key={priority}>
                                        <FormControlLabel
                                          value={priority}
                                          control={
                                            <Radio
                                              sx={{
                                                color: appColor.primary,
                                                "&.Mui-checked": {
                                                  color: appColor.primary,
                                                },
                                              }}
                                            />
                                          }
                                          label={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight:
                                                    formData.priority ===
                                                    priority
                                                      ? "bold"
                                                      : "medium",
                                                  color:
                                                    formData.priority ===
                                                    priority
                                                      ? appColor.primary
                                                      : "text.primary",
                                                }}
                                              >
                                                {priority
                                                  .split(",")[1]
                                                  .trim()
                                                  .replace(/\s*\([^)]*\)/, "")}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: appColor.primary,
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  mt: 0.5,
                                                }}
                                              >
                                                <AccessTimeIcon
                                                  sx={{ fontSize: 14, mr: 0.5 }}
                                                />
                                                {
                                                  priority.match(
                                                    /\(([^)]+)\)/
                                                  )[1]
                                                }
                                              </Typography>
                                            </Box>
                                          }
                                          sx={{
                                            width: "100%",
                                            m: 0,
                                            p: 1.5,
                                            borderRight: {
                                              xs: "none",
                                              md: "1px solid",
                                            },
                                            borderBottom: {
                                              xs: "1px solid",
                                              md: "none",
                                            },
                                            borderColor: "divider",
                                            "&:last-child": {
                                              borderRight: "none",
                                              borderBottom: "none",
                                            },
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                              bgcolor:
                                                "rgba(34, 139, 34, 0.08)",
                                              transform: "translateY(-2px)",
                                            },
                                            ...(formData.priority === priority
                                              ? {
                                                  bgcolor:
                                                    "rgba(34, 139, 34, 0.12)",
                                                  borderLeft: "4px solid",
                                                  borderLeftColor:
                                                    appColor.primary,
                                                  pl: 1.5,
                                                }
                                              : {}),
                                          }}
                                        />
                                      </Grid>
                                    ))}
                                </Grid>
                              </Box>

                              {/* Low Priority Options */}
                              <Typography
                                variant="subtitle2"
                                color="info.main"
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1.5,
                                  display: "flex",
                                  alignItems: "center",
                                  borderLeft: "4px solid",
                                  borderColor: "info.main",
                                  pl: 1.5,
                                  py: 0.5,
                                  borderRadius: "0 4px 4px 0",
                                  bgcolor: "rgba(25, 118, 210, 0.04)",
                                }}
                              >
                                <FlagIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: "info.main" }}
                                />
                                Low Priority
                              </Typography>
                              <Box
                                sx={{
                                  border: "1px solid",
                                  borderColor: "info.light",
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                  "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  },
                                }}
                              >
                                <Grid container>
                                  {priorities
                                    .filter((p) => p.startsWith("Low Priority"))
                                    .map((priority) => (
                                      <Grid item xs={12} md={4} key={priority}>
                                        <FormControlLabel
                                          value={priority}
                                          control={
                                            <Radio
                                              sx={{
                                                color: "info.main",
                                                "&.Mui-checked": {
                                                  color: "info.main",
                                                },
                                              }}
                                            />
                                          }
                                          label={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight:
                                                    formData.priority ===
                                                    priority
                                                      ? "bold"
                                                      : "medium",
                                                  color:
                                                    formData.priority ===
                                                    priority
                                                      ? "info.main"
                                                      : "text.primary",
                                                }}
                                              >
                                                {priority
                                                  .split(",")[1]
                                                  .trim()
                                                  .replace(/\s*\([^)]*\)/, "")}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "info.main",
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  mt: 0.5,
                                                }}
                                              >
                                                <AccessTimeIcon
                                                  sx={{ fontSize: 14, mr: 0.5 }}
                                                />
                                                {
                                                  priority.match(
                                                    /\(([^)]+)\)/
                                                  )[1]
                                                }
                                              </Typography>
                                            </Box>
                                          }
                                          sx={{
                                            width: "100%",
                                            m: 0,
                                            p: 1.5,
                                            borderRight: {
                                              xs: "none",
                                              md: "1px solid",
                                            },
                                            borderBottom: {
                                              xs: "1px solid",
                                              md: "none",
                                            },
                                            borderColor: "divider",
                                            "&:last-child": {
                                              borderRight: "none",
                                              borderBottom: "none",
                                            },
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                              bgcolor:
                                                "rgba(25, 118, 210, 0.08)",
                                              transform: "translateY(-2px)",
                                            },
                                            ...(formData.priority === priority
                                              ? {
                                                  bgcolor:
                                                    "rgba(25, 118, 210, 0.12)",
                                                  borderLeft: "4px solid",
                                                  borderLeftColor: "info.main",
                                                  pl: 1.5,
                                                }
                                              : {}),
                                          }}
                                        />
                                      </Grid>
                                    ))}
                                </Grid>
                              </Box>
                            </RadioGroup>
                          </FormControl>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 2, display: "block" }}
                          >
                            Due date will be automatically calculated based on
                            selected priority and complexity
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Timeline Group - Moved below Priority */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: appColor.primary,
                              mb: 2,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />{" "}
                            Timeline
                          </Typography>

                          <Grid container spacing={3}>
                            {/* Start Date Field */}
                            <Grid item xs={12} sm={6}>
                              <LocalizationProvider
                                dateAdapter={AdapterLuxon}
                                adapterLocale="en-GB"
                              >
                                <DatePicker
                                  label="Start Date"
                                  format="dd/MM/yyyy"
                                  value={
                                    formData.startDate
                                      ? DateTime.fromISO(formData.startDate)
                                      : null
                                  }
                                  onChange={(newValue) => {
                                    if (newValue && newValue.isValid) {
                                      const jsDate = newValue.toJSDate();

                                      // Check if it's a working day
                                      if (!isWorkingDay(jsDate)) {
                                        // Show warning for non-working day
                                        const selectedDateStr =
                                          jsDate.toLocaleDateString("en-GB", {
                                            weekday: "long",
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                          });

                                        // Check if it's a public holiday
                                        const holidayDetails =
                                          getHolidayDetails(jsDate);
                                        let warningMessage = "";

                                        if (holidayDetails) {
                                          warningMessage = `${selectedDateStr} is a public holiday (${holidayDetails.name}) and cannot be selected as a start date. Please choose a working day.`;
                                        } else {
                                          warningMessage = `${selectedDateStr} is a non-working day and cannot be selected. Please choose a working day (Monday-Friday, excluding holidays).`;
                                        }

                                        setWeekendDateWarning(warningMessage);

                                        setTimeout(() => {
                                          setWeekendDateWarning(null);
                                        }, 5000);
                                        return;
                                      }

                                      // Clear warning if valid
                                      setWeekendDateWarning(null);

                                      // Format date and trigger handleChange
                                      const formattedDate =
                                        newValue.toISODate();
                                      handleChange({
                                        target: {
                                          name: "startDate",
                                          value: formattedDate,
                                        },
                                      });
                                    }
                                  }}
                                  shouldDisableDate={(date) => {
                                    // Disable weekends and holidays in the calendar picker
                                    const jsDate = date.toJSDate();
                                    return !isWorkingDay(jsDate);
                                  }}
                                  minDate={DateTime.now()}
                                  slotProps={{
                                    popper: {
                                      placement: "top",
                                    },
                                    day: (ownerState) => {
                                      // Safety check for undefined day
                                      if (!ownerState || !ownerState.day) {
                                        return {};
                                      }

                                      const jsDate = ownerState.day.toJSDate();
                                      const isHoliday = isPublicHoliday(jsDate);
                                      const isWeekendDay =
                                        jsDate.getDay() === 0 ||
                                        jsDate.getDay() === 6;
                                      const holidayInfo =
                                        getHolidayDetails(jsDate);

                                      // Style for holidays (green background with emoji indicator)
                                      if (isHoliday && holidayInfo) {
                                        return {
                                          sx: {
                                            position: "relative",
                                            backgroundColor:
                                              "#1b5e20 !important",
                                            color: "#ffffff !important",
                                            fontWeight: "bold",
                                            border: "2px solid #2e7d32",
                                            content: '"🎉"',
                                            "&::after": {
                                              position: "absolute",
                                              top: "2px",
                                              right: "2px",
                                              fontSize: "8px",
                                            },
                                            "&:hover": {
                                              backgroundColor:
                                                "#2e7d32 !important",
                                              cursor: "not-allowed",
                                            },
                                            "&.Mui-disabled": {
                                              backgroundColor:
                                                "#1b5e20 !important",
                                              color: "#ffffff !important",
                                              opacity: 0.9,
                                            },
                                          },
                                        };
                                      }
                                      // Style for weekends (light gray)
                                      else if (isWeekendDay) {
                                        return {
                                          sx: {
                                            backgroundColor:
                                              "rgba(158, 158, 158, 0.1) !important",
                                            color: "#757575 !important",
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(158, 158, 158, 0.2) !important",
                                              cursor: "not-allowed",
                                            },
                                            "&.Mui-disabled": {
                                              backgroundColor:
                                                "rgba(158, 158, 158, 0.1) !important",
                                              color: "#9e9e9e !important",
                                              opacity: 0.6,
                                            },
                                          },
                                        };
                                      }

                                      return {};
                                    },
                                    textField: {
                                      fullWidth: true,
                                      required: true,
                                      error: weekendDateWarning !== null,
                                      helperText:
                                        getWeekendStatusMessage() +
                                        " - Weekends & holidays disabled in calendar",
                                      sx: {
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 2,
                                        },
                                      },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>

                            {/* Due Date Field - Disabled and auto-calculated */}
                            <Grid item xs={12} sm={6}>
                              <LocalizationProvider
                                dateAdapter={AdapterLuxon}
                                adapterLocale="en-GB"
                              >
                                <DatePicker
                                  label="Due Date"
                                  format="dd/MM/yyyy"
                                  value={
                                    formData.dueDate
                                      ? DateTime.fromISO(formData.dueDate)
                                      : null
                                  }
                                  disabled
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      required: true,
                                      helperText:
                                        "Auto-calculated based on priority and working days",
                                      sx: {
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 2,
                                        },
                                      },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>
                          </Grid>

                          {/* Color Legend for Date Picker */}
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 1,
                                fontWeight: "bold",
                                color: "text.secondary",
                              }}
                            >
                              Calendar Legend:
                            </Typography>
                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: "#1b5e20",
                                    border: "2px solid #2e7d32",
                                    borderRadius: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "10px",
                                    color: "#ffffff",
                                  }}
                                >
                                  🎉
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#1b5e20", fontWeight: "bold" }}
                                >
                                  Public Holiday
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: "rgba(158, 158, 158, 0.1)",
                                    borderRadius: 1,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#757575" }}
                                >
                                  Weekend (Sat/Sun)
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: "white",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.primary" }}
                                >
                                  Working Day
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>

                <Box hidden={activeStep !== 3}>
                  <Grid container spacing={3}>
                    {/* Task Type Field */}
                    <Grid item xs={12} sm={12}>
                      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                        <CardContent>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: appColor.primaryDark,
                              mb: 2,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <CategoryIcon fontSize="small" sx={{ mr: 1 }} />{" "}
                            Task Type
                          </Typography>

                          <FormControl
                            fullWidth
                            required
                            variant="outlined"
                            error={taskTypeError}
                          >
                            <InputLabel id="task-type-label">Type</InputLabel>
                            <Select
                              labelId="task-type-label"
                              name="taskType"
                              value={formData.taskType}
                              onChange={handleChange}
                              label="Type"
                              sx={{ borderRadius: 2 }}
                            >
                              {taskTypes.map((type) => (
                                <MenuItem
                                  key={type}
                                  value={type}
                                  sx={{
                                    borderLeft: "4px solid",
                                    borderColor:
                                      type === "Bug"
                                        ? "error.main"
                                        : type === "Idea"
                                        ? appColor.accent
                                        : type === "Improvements"
                                        ? appColor.primary
                                        : "warning.main",
                                    pl: 2,
                                  }}
                                >
                                  {type}
                                </MenuItem>
                              ))}
                            </Select>
                            {taskTypeError && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, ml: 1.5 }}
                              >
                                Please select a task type
                              </Typography>
                            )}
                          </FormControl>
                        </CardContent>
                      </Card>

                      {/* People Group */}
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: appColor.primary,
                              mb: 2,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <GroupIcon fontSize="small" sx={{ mr: 1 }} /> Team
                            Assignment
                          </Typography>

                          <Grid container spacing={2}>
                            {/* Created By Field */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Created By"
                                name="createdBy"
                                value={formData.createdBy}
                                fullWidth
                                required
                                disabled
                                InputProps={{
                                  startAdornment: (
                                    <PersonIcon
                                      sx={{ mr: 1, color: "action.active" }}
                                    />
                                  ),
                                  sx: {
                                    borderRadius: 2,
                                    bgcolor: "rgba(0, 0, 0, 0.04)",
                                  },
                                }}
                                helperText="Task creator cannot be changed"
                              />
                            </Grid>

                            {/* Assigned To Field */}
                            <Grid item xs={12} sm={6}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Autocomplete
                                  options={people}
                                  getOptionLabel={(option) => option.name || ""}
                                  value={
                                    people.find(
                                      (person) =>
                                        person.name === formData.assignedTo
                                    ) || null
                                  }
                                  onChange={(event, newValue) => {
                                    if (newValue) {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        assignedTo: newValue.name,
                                        assignedToUId: newValue.uId,
                                        assignedToUserId: newValue.userId,
                                      }));
                                    } else {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        assignedTo: "",
                                        assignedToUId: "",
                                        assignedToUserId: "",
                                      }));
                                    }
                                    // Hide sample data alert when user makes a selection
                                    if (showSampleDataAlert) {
                                      setShowSampleDataAlert(false);
                                    }
                                  }}
                                  renderOption={(props, option) => (
                                    <Box
                                      component="li"
                                      {...props}
                                      key={option.uId}
                                    >
                                      <Box>
                                        <Typography variant="body1">
                                          {option.name}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {option.uId} - {option.userId}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Assigned To"
                                      required
                                      InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                          <>
                                            <PersonIcon
                                              sx={{
                                                mr: 1,
                                                color: "action.active",
                                              }}
                                            />
                                            {params.InputProps.startAdornment}
                                          </>
                                        ),
                                        sx: { borderRadius: 2 },
                                      }}
                                      helperText="Select a staff member from the dropdown"
                                    />
                                  )}
                                  loading={peopleLoading}
                                  loadingText="Loading staff..."
                                  noOptionsText="No staff members found"
                                  disableClearable={false}
                                  sx={{ flex: 1 }}
                                />
                                <Tooltip
                                  title="Reload staff list"
                                  arrow
                                  placement="top"
                                >
                                  <IconButton
                                    onClick={fetchPeopleData}
                                    disabled={peopleLoading}
                                    sx={{
                                      mt: 1,
                                      color: appColor.primary,
                                      "&:hover": {
                                        backgroundColor: appColor.primaryFaded,
                                        transform: "rotate(180deg)",
                                        transition: "transform 0.3s ease",
                                      },
                                      "&.Mui-disabled": {
                                        color: "action.disabled",
                                      },
                                    }}
                                  >
                                    <RefreshIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Grid>

                            {/* Number of People Field */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Number of People"
                                name="numberOfPeople"
                                type="number"
                                value={formData.numberOfPeople}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Only allow numbers between 1 and 10
                                  if (
                                    value === "" ||
                                    (parseInt(value) >= 1 &&
                                      parseInt(value) <= 10)
                                  ) {
                                    handleChange({
                                      target: {
                                        name: "numberOfPeople",
                                        value: value === "" ? "1" : value,
                                      },
                                    });
                                  }
                                }}
                                fullWidth
                                InputProps={{
                                  inputProps: {
                                    min: 1,
                                    max: 10,
                                    onKeyDown: (e) => {
                                      // Prevent non-numeric input (except allowed control keys)
                                      const allowedKeys = [
                                        "Backspace",
                                        "Delete",
                                        "ArrowLeft",
                                        "ArrowRight",
                                        "Tab",
                                      ];
                                      if (
                                        !/[0-9]/.test(e.key) &&
                                        !allowedKeys.includes(e.key)
                                      ) {
                                        e.preventDefault();
                                      }
                                    },
                                  },
                                  startAdornment: (
                                    <GroupIcon
                                      sx={{ mr: 1, color: "action.active" }}
                                    />
                                  ),
                                  sx: { borderRadius: 2 },
                                }}
                                helperText="Enter a number between 1 and 10"
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Step Navigation Buttons */}
              <Grid
                item
                xs={12}
                sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}
              >
                <Button
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((prevStep) => prevStep - 1)}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  Back
                </Button>

                <Box>
                  {activeStep < steps.length - 1 ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.preventDefault(); // Prevent form submission
                        setActiveStep((prevStep) => prevStep + 1);
                      }}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        borderRadius: 2,
                        background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.primaryLight})`,
                      }}
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      startIcon={<SaveAltIcon />}
                      sx={{
                        borderRadius: 2,
                        background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.accent})`,
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
                        padding: "8px 24px",
                        "&:hover": {
                          boxShadow: "0 6px 12px rgba(0, 0, 0, 0.2)",
                        },
                      }}
                    >
                      {loading ? "Creating Task..." : "Create Task"}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>

          {/* Success Message */}
          {success && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon fontSize="inherit" />}
              sx={{
                mt: 3,
                borderRadius: 2,
                "& .MuiAlert-icon": { color: appColor.primary },
                animation: "slideUp 0.5s ease",
                "@keyframes slideUp": {
                  "0%": { opacity: 0, transform: "translateY(20px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <AlertTitle sx={{ fontWeight: "bold" }}>Success!</AlertTitle>
              Your task has been successfully created. A new form has been
              prepared for you.
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 3,
                borderRadius: 2,
              }}
            >
              <AlertTitle sx={{ fontWeight: "bold" }}>Error</AlertTitle>
              {error}
            </Alert>
          )}

          {/* Quick Jump to Step (Mobile Only) */}
          {!isSmallScreen ? null : (
            <Fab
              color="primary"
              size="medium"
              sx={{
                position: "fixed",
                bottom: 16,
                right: 16,
                display: { xs: "flex", md: "none" },
                flexDirection: "column",
                background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.primaryDark})`,
              }}
              onClick={() =>
                setActiveStep((prevStep) => (prevStep + 1) % steps.length)
              }
            >
              <ArrowForwardIcon />
            </Fab>
          )}
        </Paper>

        {/* Description Templates Modal */}
        <Dialog
          open={openDescModal}
          onClose={() => setOpenDescModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.accent})`,
              color: "white",
              padding: "16px 24px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LibraryBooksIcon />
              <Typography variant="h6">
                {formData.department} Description Templates
                <Chip
                  label={`${filteredDescTemplates.length}Templates`}
                  size="small"
                  sx={{ ml: 1, bgcolor: "rgba(255,255,255,0.2)" }}
                />
              </Typography>
            </Box>
            <IconButton
              onClick={() => setOpenDescModal(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ padding: 0 }}>
            <List>
              {filteredDescTemplates.map((template, index) => (
                <React.Fragment key={template.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleSelectDescriptionTemplate(template.id)
                      }
                      sx={{
                        padding: "16px 24px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: appColor.primaryFaded,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ color: appColor.primary }}
                          >
                            {template.label}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-line",
                              color: "text.secondary",
                              maxHeight: "80px",
                              overflow: "hidden",
                              mt: 1,
                            }}
                          >
                            {template.template}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredDescTemplates.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ padding: "16px" }}>
            <Button
              onClick={() => setOpenDescModal(false)}
              variant="outlined"
              sx={{
                borderRadius: "20px",
                color: appColor.primary,
                borderColor: appColor.primary,
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Expected Output Templates Modal */}
        <Dialog
          open={openOutputModal}
          onClose={() => setOpenOutputModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(45deg, #558b2f, #8bc34a)",
              color: "white",
              padding: "16px 24px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormatListBulletedIcon />
              <Typography variant="h6">
                {formData.department} Output Templates
                <Chip
                  label={`${filteredOutputTemplates.length} Templates`}
                  size="small"
                  sx={{ ml: 1, bgcolor: "rgba(255,255,255,0.2)" }}
                />
              </Typography>
            </Box>
            <IconButton
              onClick={() => setOpenOutputModal(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ padding: 0 }}>
            <List>
              {filteredOutputTemplates.map((template, index) => (
                <React.Fragment key={template.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleSelectOutputTemplate(template.id)}
                      sx={{
                        padding: "16px 24px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(85, 139, 47, 0.08)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="secondary"
                          >
                            {template.label}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-line",
                              color: "text.secondary",
                              maxHeight: "80px",
                              overflow: "hidden",
                              mt: 1,
                            }}
                          >
                            {template.template}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredOutputTemplates.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ padding: "16px" }}>
            <Button
              onClick={() => setOpenOutputModal(false)}
              variant="outlined"
              color="secondary"
              sx={{ borderRadius: "20px" }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Conflict Modal */}
        <Dialog
          open={openConflictModal}
          onClose={() => setOpenConflictModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(45deg, #f44336, #ff9800)",
              color: "white",
              padding: "20px 24px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <EventBusyIcon sx={{ mr: 2, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Task Scheduling Conflict Detected
              </Typography>
            </Box>
            <IconButton
              onClick={() => setOpenConflictModal(false)}
              sx={{ color: "white" }}
            />
          </DialogTitle>

          <DialogContent sx={{ py: 3, px: 4 }}>
            {conflictDetails && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    icon={<WarningAmberIcon fontSize="inherit" />}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      "& .MuiAlert-icon": {
                        color: "#f57c00",
                        alignItems: "center",
                      },
                    }}
                  >
                    <AlertTitle sx={{ fontWeight: "bold" }}>
                      Unable to Create Task
                    </AlertTitle>
                    <Typography variant="body2">
                      A task already exists for{" "}
                      <strong>{conflictDetails.PK}</strong> with the same start
                      date. Please select a different start date or assign to
                      another person.
                    </Typography>
                  </Alert>
                </Grid>

                {/* Next Available Date Card */}
                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: appColor.primary,
                      borderWidth: 2,
                      mb: 3,
                      background:
                        "linear-gradient(135deg, rgba(34, 139, 34, 0.05) 0%, rgba(34, 139, 34, 0.1) 100%)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CalendarMonthIcon
                            sx={{
                              color: appColor.primary,
                              mr: 1,
                              fontSize: 28,
                            }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: "bold",
                                color: appColor.primary,
                              }}
                            >
                              Next Available Date
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              The earliest date you can schedule this task after
                              the conflict
                            </Typography>
                          </Box>
                        </Box>

                        <Chip
                          label={
                            conflictDetails.DueDate
                              ? new Date(
                                  new Date(conflictDetails.DueDate).setDate(
                                    new Date(
                                      conflictDetails.DueDate
                                    ).getDate() + 1
                                  )
                                ).toLocaleDateString()
                              : "Not available"
                          }
                          color="primary"
                          variant="outlined"
                          size="large"
                          sx={{
                            fontWeight: "bold",
                            fontSize: "1rem",
                            py: 2,
                            px: 1,
                            borderWidth: 2,
                            borderRadius: 2,
                            background: "#fff",
                          }}
                        />
                      </Box>

                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<CalendarMonthIcon />}
                        onClick={() => {
                          // Calculate the day after the due date of the conflicting task
                          const nextAvailableDate = new Date(
                            conflictDetails.DueDate
                          );
                          nextAvailableDate.setDate(
                            nextAvailableDate.getDate() + 1
                          );

                          // Get the new start date string
                          const newStartDate = nextAvailableDate
                            .toISOString()
                            .split("T")[0];

                          // Get the days to add from the priority using the same extraction function
                          // that's used elsewhere in the code to ensure consistency
                          const daysToAdd = extractDaysFromPriority(
                            formData.priority
                          );

                          const newDueDate = new Date(nextAvailableDate);
                          newDueDate.setDate(
                            nextAvailableDate.getDate() + daysToAdd - 1
                          ); // Subtract 1 to account for inclusive counting

                          // Use the days directly from priority for TotalManDays instead of calculating
                          const totalManDays = daysToAdd;

                          // Log to ensure proper values are being sent
                          console.log("Selected Priority:", formData.priority);
                          console.log("Days to add:", daysToAdd);
                          console.log("Total man days set:", totalManDays);

                          // Update the form with the next available date and recalculated due date
                          setFormData((prevData) => ({
                            ...prevData,
                            startDate: newStartDate,
                            dueDate: newDueDate.toISOString().split("T")[0],
                            TotalManDays: totalManDays.toString(),
                          }));

                          // Close current conflict modal
                          setOpenConflictModal(false);

                          // Instead of directly submitting the form, simulate the regular submission process
                          // but use an artificial delay to ensure state is updated first
                          setTimeout(async () => {
                            try {
                              // Prepare updated payload
                              const updatedTaskPayload = {
                                ...formData,
                                startDate: newStartDate,
                                dueDate: newDueDate.toISOString().split("T")[0],
                                TotalManDays: totalManDays.toString(),
                              };

                              // Show loader
                              setShowLoader(true);
                              setLoading(true);
                              setError(null);
                              setSuccess(false);
                              setPriorityError(false);

                              // Create the task and check for conflicts
                              const response = await createNewTaskAPI(
                                userId,
                                updatedTaskPayload
                              );

                              // Handle potential conflicts same way as the main submit function
                              if (response.isAxiosError) {
                                console.log(
                                  "API Error Response:",
                                  response.response
                                );

                                if (
                                  response.response &&
                                  response.response.status === 409
                                ) {
                                  // Extract details from the error response
                                  const errorData = response.response.data;
                                  const existingTask = errorData.existingTask;

                                  // Set conflict details and show the conflict modal again
                                  setConflictDetails(existingTask);
                                  setOpenConflictModal(true);

                                  // Don't throw the error, as we'll show it in the modal
                                  setLoading(false);
                                  setShowLoader(false);
                                  return;
                                } else {
                                  // For other errors, throw a general error
                                  throw new Error(
                                    `Error: ${
                                      response.message ||
                                      "Unknown error occurred"
                                    }`
                                  );
                                }
                              }

                              if (response.status !== 200) {
                                throw new Error(
                                  `Error: ${response.statusText}`
                                );
                              }

                              console.log(
                                "Task Created Successfully:",
                                response.data
                              );

                              // 2. Update the task count in 003_People_Master table
                              try {
                                const loginIdToUpdate =
                                  updatedTaskPayload.assignedToUserId ||
                                  "uid0031@auj.com";

                                const { data: currentData, error: fetchError } =
                                  await supabase
                                    .from("003_People_Master")
                                    .select("task_to_do_count")
                                    .eq("login_id", loginIdToUpdate)
                                    .single();

                                if (fetchError) throw fetchError;

                                const currentCount =
                                  currentData?.task_to_do_count || 0;
                                const newCount = currentCount + 1;

                                const { error: updateError } = await supabase
                                  .from("003_People_Master")
                                  .update({ task_to_do_count: newCount })
                                  .eq("login_id", loginIdToUpdate);

                                if (updateError) throw updateError;
                              } catch (countError) {
                                console.error(
                                  "Error updating task count:",
                                  countError
                                );
                              }

                              setSuccess(true);

                              // Store task data for PDF generation and show the PDF preview dialog
                              const taskDataForPdf = {
                                ...updatedTaskPayload,
                                TaskID:
                                  response.data?.TaskID ||
                                  `TASK-${Math.floor(
                                    100000 + Math.random() * 900000
                                  )}`,
                                CreatedAt: new Date().toISOString(),
                              };
                              setPdfTaskData(taskDataForPdf);
                              setOpenPdfDialog(true);

                              // Reset form with sample data
                              resetWithSampleData();
                            } catch (err) {
                              console.error("Error creating task:", err);
                              setError(err.message);
                            } finally {
                              setLoading(false);
                              setShowLoader(false);
                            }
                          }, 300);
                        }}
                        sx={{
                          mt: 2,
                          borderRadius: 2,
                          py: 1,
                          background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.primaryLight})`,
                          boxShadow: "0 4px 10px rgba(34, 139, 34, 0.25)",
                          "&:hover": {
                            bgcolor: "#1976d2",
                            boxShadow: "0 6px 12px rgba(34, 139, 34, 0.35)",
                          },
                        }}
                      >
                        Use This Date
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: "divider",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      overflow: "visible",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -15,
                        left: 24,
                        bgcolor: "#f5f5f5",
                        px: 1,
                        borderRadius: 1,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Typography variant="subtitle1" color="text.secondary">
                        Existing Task Details
                      </Typography>
                    </Box>

                    <CardContent sx={{ pt: 4 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          color: "text.primary",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <TaskAltIcon sx={{ mr: 1, color: appColor.primary }} />
                        {conflictDetails.Title}
                        <Chip
                          label={conflictDetails.TaskID}
                          size="small"
                          sx={{
                            ml: 2,
                            bgcolor: "#e3f2fd",
                            color: "#1976d2",
                            fontWeight: "bold",
                          }}
                        />
                      </Typography>

                      <Typography
                        variant="body2"
                        paragraph
                        sx={{ color: "text.secondary", mb: 3 }}
                      >
                        {conflictDetails.Description}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Timeline Information */}
                        <Grid item xs={12} md={6}>
                          <Card
                            variant="outlined"
                            sx={{ borderRadius: 2, bgcolor: "#f9f9f9" }}
                          >
                            <CardContent>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: appColor.primary,
                                  mb: 2,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <AccessTimeIcon
                                  fontSize="small"
                                  sx={{ mr: 1 }}
                                />{" "}
                                Timeline
                              </Typography>

                              <Stack spacing={2}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <CalendarMonthIcon
                                      fontSize="small"
                                      sx={{ mr: 1, color: "text.secondary" }}
                                    />
                                    Start Date:
                                  </Typography>
                                  <Chip
                                    label={new Date(
                                      conflictDetails.StartDate
                                    ).toLocaleDateString()}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ borderRadius: 1 }}
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <ScheduleIcon
                                      fontSize="small"
                                      sx={{ mr: 1, color: "text.secondary" }}
                                    />
                                    Due Date:
                                  </Typography>
                                  <Chip
                                    label={new Date(
                                      conflictDetails.DueDate
                                    ).toLocaleDateString()}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    sx={{ borderRadius: 1 }}
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <PriorityHighIcon
                                      fontSize="small"
                                      sx={{ mr: 1, color: "text.secondary" }}
                                    />
                                    Priority:
                                  </Typography>
                                  <Chip
                                    label={conflictDetails.Priority}
                                    size="small"
                                    color={
                                      conflictDetails.Priority === "High"
                                        ? "warning"
                                        : conflictDetails.Priority ===
                                          "Critical"
                                        ? "error"
                                        : "default"
                                    }
                                    sx={{ borderRadius: 1 }}
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    Status:
                                  </Typography>
                                  <Chip
                                    label={conflictDetails.Status}
                                    size="small"
                                    color={
                                      conflictDetails.Status === "Progress"
                                        ? "info"
                                        : conflictDetails.Status === "Done"
                                        ? "success"
                                        : "default"
                                    }
                                    sx={{ borderRadius: 1 }}
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <PersonIcon
                                      fontSize="small"
                                      sx={{ mr: 1, color: "text.secondary" }}
                                    />
                                    Assigned To:
                                  </Typography>
                                  <Chip
                                    label={conflictDetails.PK.split("@")[0]}
                                    size="small"
                                    sx={{
                                      borderRadius: 1,
                                      bgcolor: appColor.primaryFaded,
                                      color: appColor.primary,
                                    }}
                                  />
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Other Information */}
                        <Grid item xs={12} md={6}>
                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              bgcolor: "#f9f9f9",
                              height: "100%",
                            }}
                          >
                            <CardContent>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: appColor.accent,
                                  mb: 2,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <CategoryIcon fontSize="small" sx={{ mr: 1 }} />{" "}
                                Task Information
                              </Typography>

                              <Stack spacing={2}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    Task Type:
                                  </Typography>
                                  <Chip
                                    label={
                                      conflictDetails.TaskType ||
                                      "Not specified"
                                    }
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 1,
                                      color:
                                        conflictDetails.TaskType === "Bug"
                                          ? "error.main"
                                          : conflictDetails.TaskType ===
                                            "Feature"
                                          ? "secondary.main"
                                          : conflictDetails.TaskType ===
                                            "Improvements"
                                          ? appColor.primary
                                          : "warning.main",
                                    }}
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    Man Days:
                                  </Typography>
                                  <Chip
                                    label={
                                      conflictDetails.TotalManDays ||
                                      "Not specified"
                                    }
                                    size="small"
                                    sx={{ borderRadius: 1 }}
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    Created At:
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {new Date(
                                      conflictDetails.CreatedAt
                                    ).toLocaleString()}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    Expected Output:
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "block",
                                      bgcolor: "rgba(0,0,0,0.03)",
                                      borderRadius: 1,
                                      p: 1,
                                    }}
                                  >
                                    {conflictDetails.ExpectedOutput ||
                                      "Not specified"}
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </DialogContent>

          <DialogActions
            sx={{ p: 3, display: "flex", justifyContent: "space-between" }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setOpenConflictModal(false)}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>

            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenConflictModal(false)}
                sx={{
                  borderRadius: 2,
                  background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.accent})`,
                  px: 3,
                }}
              >
                OK, I'll Change My Task
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* PDF Preview Confirmation Dialog */}
        <Dialog
          open={openPdfDialog}
          onClose={() => setOpenPdfDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.accent})`,
              color: "white",
              padding: "20px 24px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <PictureAsPdfIcon sx={{ mr: 2, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                View Task as PDF
              </Typography>
            </Box>
            <IconButton
              onClick={() => setOpenPdfDialog(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 3, px: 4, mt: 2 }}>
            <Typography variant="body1" paragraph>
              Would you like to view and print this task assignment as a PDF
              document?
            </Typography>

            <Typography variant="body2" color="text.secondary">
              The PDF includes all task details and can be printed or shared
              with the assigned team members.
            </Typography>

            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "rgba(34, 139, 34, 0.05)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              <FileDownloadIcon sx={{ mr: 2, color: appColor.primary }} />
              <Typography variant="body2" color="text.secondary">
                The task PDF will open in a new browser tab for easy printing or
                downloading.
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => handlePdfDownload()}
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#2196f3",
                    boxShadow: "0 4px 10px rgba(33, 150, 243, 0.25)",
                    "&:hover": {
                      bgcolor: "#1976d2",
                      boxShadow: "0 6px 12px rgba(33, 150, 243, 0.35)",
                    },
                    py: 1.5,
                  }}
                >
                  Download PDF
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ShareIcon />}
                  onClick={() => handleSharePdf()}
                  sx={{
                    borderRadius: 2,
                    bgcolor: appColor.accent,
                    boxShadow: `0 4px 10px ${
                      appColor.accentFaded || "rgba(0, 0, 0, 0.25)"
                    }`,
                    "&:hover": {
                      bgcolor: appColor.accentDark || "#558b2f",
                      boxShadow: `0 6px 12px ${
                        appColor.accentFaded || "rgba(0, 0, 0, 0.35)"
                      }`,
                    },
                    py: 1.5,
                  }}
                >
                  Share PDF
                </Button>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{ p: 3, display: "flex", justifyContent: "space-between" }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setOpenPdfDialog(false)}
              sx={{ borderRadius: 2 }}
            >
              No, Thanks
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handlePdfPreview}
              startIcon={<PictureAsPdfIcon />}
              sx={{
                borderRadius: 2,
                background: `linear-gradient(45deg, ${appColor.primary}, ${appColor.accent})`,
                px: 3,
              }}
            >
              Yes, Show PDF
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default CreateTask;
